import { ConfidenceChip } from "./ConfidenceChip";
import type {
  ConfidenceBasisKey,
  ConfidenceLevel,
  SimulationResult,
  TrackRecordSourceKey,
} from "@opus-dt/shared-types";

interface ResultKpisProps {
  results: NonNullable<SimulationResult["results"]>;
  /** O nível EXIBIDO (Spec 079): da régua quando ela existe, da procedência quando não. */
  confidenceLevel: ConfidenceLevel | null;
  confidenceBasis?: ConfidenceBasisKey | null;
  trackRecordSource?: TrackRecordSourceKey | null;
}

// Desempenho e OEE passaram a ser ANULÁVEIS: sem plano declarado não há
// denominador, e o motor devolve nulo em vez de inventar um número. A regra de renderização é a
// que a plataforma já usa para KPI ausente (`ScenariosTable`, `_vitals-oee-hero`): "—", NUNCA `0`.
// Zero aqui seria pior que feio: no ranking do lote, um candidato sem plano apareceria como um
// candidato de desempenho péssimo, e não como um candidato que ninguém mediu.
const NO_VALUE = "—";

// `!= null` e não `=== null`: `results` é Json persistido, e uma rodada antiga (ou parcial) pode
// simplesmente não ter a chave — `undefined.toFixed` explodiria a tela.
const hasValue = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value);

// KPIs do resultado: OEE + A/P/Q + produção + confiança (chip+dot+label).
export function ResultKpis({
  results,
  confidenceLevel,
  confidenceBasis,
  trackRecordSource,
}: ResultKpisProps) {
  // A célula carrega o valor CRU: a ausência é decidida uma vez, aqui, e não recuperada depois por
  // comparação de string contra o travessão (que quebraria em silêncio se o glifo mudasse).
  const cells: Array<{ label: string; value: number | null | undefined; big?: boolean }> = [
    { label: "OEE", value: results.oee, big: true },
    { label: "Disponibilidade", value: results.availability },
    { label: "Desempenho", value: results.performance },
    { label: "Qualidade", value: results.quality },
  ];
  // Uma linha só, e só quando faz falta: "—" sem explicação faz o gestor achar que a rodada
  // falhou. Mas o motivo NÃO é sempre o mesmo — o motor também devolve nulo quando a rodada não
  // teve máquina ativa ou ficou totalmente travada (`availability = 0`), e aí culpar o plano manda
  // o gestor para o lugar errado. Só afirmamos "faltou plano" quando a linha de fato operou.
  const semMedida = !hasValue(results.performance) || !hasValue(results.oee);
  const semPlano = semMedida && results.availability > 0;
  return (
    <>
      <dl data-testid="result-kpis" className="grid grid-cols-2 gap-3 text-sm">
        {cells.map((c) => (
          <div key={c.label}>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</dt>
            <dd
              className={
                c.big
                  ? "font-mono text-2xl font-semibold tabular-nums"
                  : "font-mono text-base tabular-nums"
              }
            >
              {hasValue(c.value) ? (
                `${c.value.toFixed(1)}%`
              ) : (
                <span className="text-muted-foreground">{NO_VALUE}</span>
              )}
            </dd>
          </div>
        ))}
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">
            Produção estimada
          </dt>
          <dd className="font-mono text-base tabular-nums">
            {results.estimatedProduction.toLocaleString("pt-BR")}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">Confiança</dt>
          <dd className="mt-0.5">
            <ConfidenceChip
              level={confidenceLevel}
              basis={confidenceBasis}
              trackRecordSource={trackRecordSource}
            />
          </dd>
        </div>
      </dl>
      {semPlano ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Desempenho e OEE aparecem como “—” porque esta rodada não recebeu um plano de produção
          para servir de referência. Disponibilidade e Qualidade não dependem de plano.
        </p>
      ) : null}
    </>
  );
}
